import { useState, useMemo } from 'react';
import { useAuth } from '@/contexts/auth';
import { useTasksQuery } from './queries/use-tasks-query';
import { useAddTaskMutation } from './mutations/use-add-task-mutation';
import { useCompleteTaskMutation, useUncompleteTaskMutation } from './mutations/use-complete-task-mutation';
import { useDeleteTaskMutation } from './mutations/use-delete-task-mutation';
import { useUpdateTaskMutation } from './mutations/use-update-task-mutation';
import { Task } from '@/types/tasks';

// Change this line from export { DateRangeOption } to export type { DateRangeOption }
export type { DateRangeOption } from './types/task-operations-types';

// Stable empty arrays to prevent infinite re-renders
const EMPTY_ACTIVE_TASKS: Task[] = [];
const EMPTY_COMPLETED_TASKS: Task[] = [];

export const useTaskOperations = (
  dateRange: string = 'week', 
  currentDate: Date = new Date(), 
  strictDateRange: boolean = false,
  noDateFilter: boolean = false // NEW: Option to bypass date filtering
) => {
  const { user } = useAuth(); // Now uses centralized auth context
  const [error, setError] = useState<string | null>(null);

  // Use our refactored task query hook
  const { 
    data: tasks,
    isLoading,
    isError,
    refetch
  } = useTasksQuery(user?.id, dateRange as any, currentDate, strictDateRange, noDateFilter);
  
  // Use our refactored task mutation hooks
  const addTaskMutation = useAddTaskMutation(user?.id);
  const completeTaskMutation = useCompleteTaskMutation(user?.id);
  const uncompleteTaskMutation = useUncompleteTaskMutation(user?.id);
  const removeTaskMutation = useDeleteTaskMutation(user?.id);
  const updateTaskMutation = useUpdateTaskMutation(user?.id);

  // Stabilize array references to prevent infinite loops
  const activeTasks = useMemo(() => 
    tasks?.active || EMPTY_ACTIVE_TASKS, 
    [tasks?.active]
  );
  
  const completedTasks = useMemo(() => 
    tasks?.completed || EMPTY_COMPLETED_TASKS, 
    [tasks?.completed]
  );

  return {
    activeTasks,
    completedTasks,
    isLoading,
    isError,
    error,
    dateRange,
    currentDate,
    addTask: addTaskMutation.mutate,
    completeTask: completeTaskMutation.mutate,
    uncompleteTask: uncompleteTaskMutation.mutate,
    removeTask: removeTaskMutation.mutate,
    updateTask: updateTaskMutation.mutate,
    refetch
  };
};

export default useTaskOperations;
